import { getPlatformSlug, SHOPIFY_SLUG } from './ecommercesPlatforms';

/*
 * Landing the seller back on the integration they were connecting.
 *
 * The backend's OAuth callbacks always redirect to the listing
 * (`${FRONT_URL}configuration/ecommerce`): Mercado Libre with no query at all,
 * Shopify with `?status=success&shop=…` or `?error=…`. Both init endpoints do
 * answer with the record they created, so its id is known before the seller
 * leaves — it is written down here and read back on return.
 *
 * `sessionStorage` rather than the store: the trip through the platform reloads
 * the app, and the store only persists `auth`. It is also per tab, which is the
 * tab the redirect comes back to.
 */

const STORAGE_KEY = 'nh:ecommerce-oauth';

/** A mark older than this is from an abandoned attempt, not this return. */
export const OAUTH_MARK_TTL_MS = 30 * 60 * 1000;

// Reading `window.sessionStorage` itself throws when storage is blocked.
const getStorage = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/** A page of this app, never an address elsewhere. */
const isAppPath = (path) => typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');

/**
 * @param {{id: number, platform: string, returnTo?: string}} mark - `returnTo`
 *   when the authorization started away from the integration: a warehouse
 *   screen asking the store for permissions again.
 * @param {number} [now]
 */
export const rememberOauthStart = ({ id, platform, returnTo }, now = Date.now()) => {
  try {
    const mark = { id, platform, startedAt: now, ...(isAppPath(returnTo) ? { returnTo } : {}) };
    getStorage()?.setItem(STORAGE_KEY, JSON.stringify(mark));
  } catch {
    // Without storage the return falls back to the query, or stays on the listing.
  }
};

/** Reads the mark and removes it, so it answers one return only. */
const takeMark = (now) => {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(STORAGE_KEY);
    storage.removeItem(STORAGE_KEY);
    if (!raw) return null;

    const mark = JSON.parse(raw);
    if (!mark?.id || now - mark.startedAt > OAUTH_MARK_TTL_MS) return null;
    return mark;
  } catch {
    return null;
  }
};

const normalizeShop = (url) =>
  String(url || '')
    .replace('https://', '')
    .replace('http://', '')
    .replace(/\/+$/, '')
    .trim()
    .toLowerCase();

/**
 * The integration a return from OAuth belongs to, and the page it started
 * from when that was not the integration, or `null` for a normal visit.
 *
 * Call it once the listing is loaded: the mark is consumed on the first call.
 *
 * 1. The mark left by `rememberOauthStart`, when that integration exists.
 * 2. Without one, Shopify's `?shop=` against the stored shop URL.
 * 3. Nothing else: a visit with neither stays on the listing.
 *
 * @param {object} params
 * @param {string} [params.search] - `location.search`.
 * @param {Array<object>} params.integrations
 * @param {number} [params.now]
 * @returns {{id: number|string, returnTo: string|null}|null}
 */
export const resolveOauthReturn = ({ search = '', integrations = [], now = Date.now() }) => {
  const mark = takeMark(now);
  if (mark && integrations.some((integration) => String(integration.id) === String(mark.id))) {
    return { id: mark.id, returnTo: isAppPath(mark.returnTo) ? mark.returnTo : null };
  }

  const shop = new URLSearchParams(search).get('shop');
  if (shop) {
    const match = integrations.find(
      (integration) =>
        getPlatformSlug(integration.name) === SHOPIFY_SLUG &&
        normalizeShop(integration.configuration?.authentication?.shop_url) === normalizeShop(shop)
    );
    if (match) return { id: match.id, returnTo: null };
  }

  return null;
};

/** Messages for the `?error=` codes of `shopify_auth_callback`. */
export const SHOPIFY_RETURN_ERRORS = {
  missing_params: 'Shopify no devolvió los datos de la autorización. Vuelve a conectar.',
  shopify_rejected: 'Shopify rechazó la autorización. Revisa el Client ID y el Client Secret.',
  config_not_found: 'No encontramos la integración de Shopify que se estaba conectando.',
  internal_server_error: 'No se pudo completar la conexión con Shopify. Intenta nuevamente.',
};

/**
 * The notice a return carries in its query, if any.
 *
 * @param {string} search
 * @returns {{type: 'success'|'error', message: string}|null}
 */
export const getOauthReturnNotice = (search) => {
  const params = new URLSearchParams(search);
  const error = params.get('error');
  if (error) {
    return {
      type: 'error',
      message: SHOPIFY_RETURN_ERRORS[error] || 'No se pudo completar la conexión.',
    };
  }
  if (params.get('status') === 'success') {
    return { type: 'success', message: 'Shopify quedó conectado.' };
  }
  return null;
};
